import React, { useEffect, useState } from "react";
import { DeleteJob, GetMyJobs } from "../../../Redux/Slices/JobPost";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate, formatTimeAgo } from "../../../Helper/FromatTime";
import { FaFilter, FaSearch, FaTrash, FaTimesCircle, FaBriefcase, FaBookmark, FaCalendarAlt, FaBuilding, FaUsers } from "react-icons/fa";
import { BsInboxes } from "react-icons/bs";
import "./AppliedJobs.css";
import toast from "react-hot-toast";

const ConfirmationModal = ({ show, onClose, onConfirm, title, message }) => {
  if (!show) return null;

  return (
    <div className="confirmation-modal" onClick={onClose}>
      <div className="confirmation-content" onClick={(e) => e.stopPropagation()}>
        <div className="confirmation-icon">
          <FaTrash />
        </div>
        <h3 className="confirmation-title">{title}</h3>
        <p className="confirmation-message">{message}</p>
        <div className="confirmation-actions">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="confirm-btn" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const AppliedJobsModern = () => {
  const location = useLocation();
  const initialTab = location.state?.tab === "saved" ? "saved" : "applied";
  const [activeTab, setActiveTab] = useState(initialTab);
  const { myjobs } = useSelector((state) => state?.Jobs);
  const userId = localStorage.getItem("id");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  useEffect(() => {
    dispatch(GetMyJobs(userId));
    document.title = "My Jobs | CAJobPortal";
  }, [dispatch, userId]);

  useEffect(() => {
    if (location.state?.tab === "saved" || location.state?.tab === "applied") {
      setActiveTab(location.state.tab);
    }
  }, [location.state]);

  // Filter and sort jobs
  const getFilteredJobs = () => {
    if (!myjobs) return [];

    let filtered = myjobs.filter((item) => {
      // Tab filter
      if (activeTab === "applied" && item.type !== "applied") return false;
      if (activeTab === "saved" && (item.type !== "saved" || !item.is_active)) return false;

      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const title = item?.job_id?.title?.toLowerCase() || "";
        const company = item?.job_id?.company_name?.toLowerCase() || "";
        if (!title.includes(search) && !company.includes(search)) return false;
      }

      // Status filter
      if (statusFilter && activeTab === "applied") {
        const status = item?.action_status?.toLowerCase() || "pending";
        if (status !== statusFilter.toLowerCase()) return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "date_desc":
          return new Date(b.created_at) - new Date(a.created_at);
        case "date_asc":
          return new Date(a.created_at) - new Date(b.created_at);
        case "company":
          return (a?.job_id?.company_name || "").localeCompare(b?.job_id?.company_name || "");
        case "title":
          return (a?.job_id?.title || "").localeCompare(b?.job_id?.title || "");
        default:
          return 0;
      }
    });

    return filtered;
  };

  const handleDelete = (job) => {
    setJobToDelete(job);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (jobToDelete) {
      try {
        await DeleteJob(jobToDelete._id);
        toast.success("Job removed successfully");
        dispatch(GetMyJobs(userId));
        setShowConfirmModal(false);
        setJobToDelete(null);
      } catch (error) {
        toast.error("Failed to remove job");
      }
    }
  };

  const viewJob = (jobId) => {
    navigate(`/job-description/${jobId}`);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setSortBy("date_desc");
  };

  const filteredJobs = getFilteredJobs();
  const appliedCount = myjobs?.filter((item) => item.type === "applied").length || 0;
  const savedCount = myjobs?.filter((item) => item.type === "saved" && item.is_active).length || 0;

  return (
    <div className="applied-jobs-page">
      <div className="applied-jobs-container">
        {/* Header */}
        <div className="applied-jobs-header">
          <div className="header-top">
            <h1 className="page-title">My Jobs</h1>
            <div className="job-stats">
              <div className="stat-badge">
                Applied: <span className="stat-number">{appliedCount}</span>
              </div>
              <div className="stat-badge">
                Saved: <span className="stat-number">{savedCount}</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <ul className="job-tabs">
            <li
              className={`tab-item ${activeTab === "applied" ? "active" : ""}`}
              onClick={() => setActiveTab("applied")}
            >
              <FaBriefcase style={{ marginRight: "0.375rem" }} />
              Applied Jobs
              <span className="tab-count">{appliedCount}</span>
            </li>
            <li
              className={`tab-item ${activeTab === "saved" ? "active" : ""}`}
              onClick={() => setActiveTab("saved")}
            >
              <FaBookmark style={{ marginRight: "0.375rem" }} />
              Saved Jobs
              <span className="tab-count">{savedCount}</span>
            </li>
          </ul>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-header">
            <h3 className="filters-title">
              <FaFilter />
              Filters
            </h3>
            <button className="clear-filters" onClick={clearFilters}>
              Clear All
            </button>
          </div>

          <div className="filters-grid">
            {/* Search */}
            <div className="filter-group">
              <label className="filter-label">Search</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Job title or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: "2.5rem" }}
                />
                <FaSearch
                  style={{
                    position: "absolute",
                    left: "0.875rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9ca3af",
                    fontSize: "0.875rem",
                  }}
                />
              </div>
            </div>

            {/* Status Filter (Applied Tab Only) */}
            {activeTab === "applied" && (
              <div className="filter-group">
                <label className="filter-label">Application Status</label>
                <select
                  className="filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="viewed">Viewed</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            )}

            {/* Sort By */}
            <div className="filter-group">
              <label className="filter-label">Sort By</label>
              <select
                className="filter-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="company">Company Name</option>
                <option value="title">Job Title</option>
              </select>
            </div>
          </div>
        </div>

        {/* Jobs List */}
        {filteredJobs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <BsInboxes />
            </div>
            <h3 className="empty-title">
              {searchQuery || statusFilter
                ? "No jobs match your filters"
                : activeTab === "applied"
                ? "You haven't applied to any jobs yet"
                : "You haven't saved any jobs yet"}
            </h3>
            <p className="empty-message">
              {searchQuery || statusFilter
                ? "Try adjusting your filters to see more results"
                : "Start exploring job opportunities and build your career!"}
            </p>
            <button
              className="browse-jobs-btn"
              onClick={() => navigate("/jobs")}
            >
              Browse Jobs
            </button>
          </div>
        ) : (
          <div className="jobs-list">
            {filteredJobs.map((item, index) => (
              <div
                key={item._id || index}
                className="job-card"
                onClick={() => viewJob(item?.job_id?._id)}
              >
                <div className="job-card-header">
                  <div className="job-card-info">
                    <h3 className="job-card-title">{item?.job_id?.title || "N/A"}</h3>
                    <p className="job-card-company">{item?.job_id?.company_name || "N/A"}</p>
                  </div>
                  <div className="job-card-actions" onClick={(e) => e.stopPropagation()}>
                    {activeTab === "saved" && (
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(item)}
                        title="Remove from saved"
                      >
                        <FaTrash />
                        Remove
                      </button>
                    )}
                    {activeTab === "applied" && (
                      <span className={`status-badge ${item?.action_status?.toLowerCase() || "applied"}`}>
                        {item?.action_status || "Applied"}
                      </span>
                    )}
                  </div>
                </div>

                <div className="job-card-meta">
                  <div className="meta-item">
                    <FaCalendarAlt />
                    <span>
                      {activeTab === "applied" ? "Applied" : "Saved"} on {formatDate(item?.created_at)}
                    </span>
                  </div>
                  {item?.job_id?.cities?.[0] && (
                    <div className="meta-item">
                      <FaBuilding />
                      <span>{item.job_id.cities[0]}</span>
                    </div>
                  )}
                </div>

                <div className="job-card-stats">
                  {activeTab === "applied" && item?.updated_by?.last_login && (
                    <div className="stat-item">
                      <span className="stat-label">Employer Last Active</span>
                      <span className="stat-value">
                        {formatTimeAgo(new Date(item.updated_by.last_login))}
                      </span>
                    </div>
                  )}
                  {activeTab === "saved" && item?.created_by?.last_login && (
                    <div className="stat-item">
                      <span className="stat-label">Employer Last Active</span>
                      <span className="stat-value">
                        {formatTimeAgo(new Date(item.created_by.last_login))}
                      </span>
                    </div>
                  )}
                  <div className="stat-item">
                    <span className="stat-label">Total Applicants</span>
                    <span className="stat-value">
                      {item?.job_id?.applications?.length || 0} {item?.job_id?.applications?.length === 1 ? 'person' : 'people'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setJobToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Remove Saved Job?"
        message="Are you sure you want to remove this job from your saved list? This action cannot be undone."
      />
    </div>
  );
};

export default AppliedJobsModern;
